/* Write paths for the PDP catalog (designs/pdp-catalog.md PC13/PC16). Each
   posts or patches, and on success re-reads the three lists — the server is
   the authority on what the catalog now holds. A refusal comes back as the
   server's own reason (verbatim) for the form's `.form-error`; null = saved.
   The client validated with the same shared rules first, so the server's
   answer is normally the one the reader already saw sooner (AA2). */
import type {
  PdpCriterionEdit,
  PdpCriterionInput,
  PdpDeliverableEdit,
  PdpDeliverableInput,
  PdpGateEdit,
} from "../../../shared/pdp";
import type { Store } from "../shell/store";

const FAILED = "Save failed — is the server healthy?";

async function failureReason(res: { status?: number; json(): Promise<unknown> }): Promise<string> {
  try {
    const { error } = (await res.json()) as { error?: string };
    if (error) return error;
  } catch {
    /* no readable body — fall through to the status */
  }
  if (res.status === 401 || res.status === 403)
    return `Save failed — the request was refused before it reached the app; reload the page to sign in again. (HTTP ${res.status})`;
  return res.status === undefined ? FAILED : `${FAILED} (HTTP ${res.status})`;
}

async function write(store: Store, path: string, method: "POST" | "PATCH", body: unknown): Promise<string | null> {
  try {
    const res = await fetch(path, {
      method,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return failureReason(res);
  } catch {
    return FAILED;
  }
  await store.reloadPdp();
  return null;
}

export const createDeliverable = (store: Store, input: PdpDeliverableInput): Promise<string | null> =>
  write(store, "/api/pdp/deliverables", "POST", input);

export const patchDeliverable = (store: Store, id: string, edit: PdpDeliverableEdit): Promise<string | null> =>
  write(store, `/api/pdp/deliverables/${encodeURIComponent(id)}`, "PATCH", edit);

export const createCriterion = (store: Store, input: PdpCriterionInput): Promise<string | null> =>
  write(store, "/api/pdp/criteria", "POST", input);

export const patchCriterion = (store: Store, id: string, edit: PdpCriterionEdit): Promise<string | null> =>
  write(store, `/api/pdp/criteria/${encodeURIComponent(id)}`, "PATCH", edit);

export const patchGate = (store: Store, id: string, edit: PdpGateEdit): Promise<string | null> =>
  write(store, `/api/pdp/gates/${encodeURIComponent(id)}`, "PATCH", edit);
